import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import api from "../api/axiosConfig";

const CARD_BG = "#0b1522";
const SURFACE = "#0e1a2b";
const BORDER = "rgba(120,140,180,.18)";
const TEXT_MAIN = "#eaf2ff";
const TEXT_SUB = "#9fb4d8";
const PRIMARY_GRAD = "linear-gradient(90deg,#3ec7ff,#447dff)";

const Card = styled(Paper)({
  borderRadius: 18,
  padding: 24,
  border: `1px solid ${BORDER}`,
  background: CARD_BG,
  boxShadow: "0 18px 40px rgba(8,20,40,.45)",
});

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: SURFACE,
    color: TEXT_MAIN,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#7fb0ff" },
    "&.Mui-focused fieldset": {
      borderColor: "#7fb0ff",
      boxShadow: "0 0 0 3px rgba(127,176,255,.18)",
    },
    "& input": { color: TEXT_MAIN },
  },
  "& .MuiFormHelperText-root": { marginLeft: 0, color: TEXT_SUB },
};

const btnPrimary = {
  height: 46,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: "none" as const,
  color: "#0b1522",
  background: PRIMARY_GRAD,
  border: "1px solid rgba(62,199,255,.4)",
  boxShadow: "0 8px 20px rgba(62,199,255,.25)",
  "&:hover": { filter: "brightness(1.05)" },
};

const btnOutline = {
  height: 46,
  borderRadius: 12,
  fontWeight: 800,
  textTransform: "none" as const,
  color: TEXT_MAIN,
  border: `1px solid ${BORDER}`,
  background: "transparent",
};

const safeTrim = (s: string) => (s ?? "").trim();
const nfkc = (s: string) => (s ?? "").normalize("NFKC");

const hasTripleRepeat = (pw: string) => /(.)\1\1/.test(pw);

const hasSequentialRun = (pw: string, len = 3) => {
  const toCode = (c: string) => c.charCodeAt(0);
  for (let i = 0; i <= pw.length - len; i++) {
    const slice = pw.slice(i, i + len);
    let asc = true;
    let desc = true;
    for (let k = 1; k < slice.length; k++) {
      if (toCode(slice[k]) !== toCode(slice[k - 1]) + 1) asc = false;
      if (toCode(slice[k]) !== toCode(slice[k - 1]) - 1) desc = false;
      if (!asc && !desc) break;
    }
    if (asc || desc) return true;
  }
  return false;
};

const categoriesCount = (pw: string) =>
  [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].reduce(
    (c, r) => c + (r.test(pw) ? 1 : 0),
    0
  );

const validatePasswordPolicy = (pwRaw: string) => {
  const pw = nfkc(safeTrim(pwRaw));
  const msgs: string[] = [];
  if (pw.length < 8) msgs.push("8자 이상");
  if (categoriesCount(pw) < 3) msgs.push("대/소문자/숫자/특수 3종 이상");
  if (hasTripleRepeat(pw)) msgs.push("동일문자 3회 반복 금지");
  if (hasSequentialRun(pw)) msgs.push("연속문자 3자 이상 금지");
  return { ok: msgs.length === 0, message: msgs.join(" · ") };
};

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showPw, setShowPw] = useState(false);

  const passwordPolicyText = useMemo(
    () => "8자 이상 · 대/소문자/숫자/특수 3종 이상 · 연속/반복 금지",
    []
  );

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setValid(false);
        setChecking(false);
        return;
      }

      try {
        const res = await api.get("/user/validate-reset-token", {
          params: { token },
        });
        setValid(Boolean(res.data?.valid));
      } catch {
        setValid(false);
      } finally {
        setChecking(false);
      }
    };

    run();
  }, [token]);

  const submit = async () => {
    setError(null);
    setSuccess(null);

    if (!token) {
      setError("유효하지 않은 접근입니다.");
      return;
    }

    const v = validatePasswordPolicy(password);
    if (!v.ok) {
      setError(v.message);
      return;
    }

    if (password !== confirm) {
      setError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    try {
      setLoading(true);
      await api.post("/user/reset-password", {
        token,
        newPassword: nfkc(safeTrim(password)),
      });
      setSuccess("비밀번호가 변경되었습니다. 다시 로그인해주세요.");
      setTimeout(() => navigate("/"), 1200);
    } catch (e: any) {
      const message =
        e?.response?.data?.message || "비밀번호 변경에 실패했습니다.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#020814",
        color: TEXT_MAIN,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: { xs: 10, md: 14 },
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card>
          <Typography sx={{ fontSize: 24, fontWeight: 900, mb: 1 }}>
            비밀번호 재설정
          </Typography>
          <Typography sx={{ color: TEXT_SUB, mb: 3 }}>
            새 비밀번호를 입력해 계정 비밀번호를 변경하세요.
          </Typography>

          {checking ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={22} />
              <Typography sx={{ color: TEXT_SUB }}>
                재설정 링크를 확인하는 중입니다.
              </Typography>
            </Box>
          ) : !valid ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              유효하지 않거나 만료된 링크입니다.
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                type={showPw ? "text" : "password"}
                placeholder="새 비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={fieldSx}
                helperText={passwordPolicyText}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPw((v) => !v)}
                        size="small"
                        sx={{ color: TEXT_SUB }}
                      >
                        {showPw ? (
                          <VisibilityOffRoundedIcon fontSize="small" />
                        ) : (
                          <VisibilityRoundedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={showPw ? "text" : "password"}
                placeholder="새 비밀번호 확인"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                sx={{ ...fieldSx, mt: 1.5 }}
                disabled={loading}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1.25, mt: 3 }}>
                <Button
                  fullWidth
                  sx={btnOutline}
                  disabled={loading}
                  onClick={() => navigate("/")}
                >
                  홈으로
                </Button>
                <Button
                  fullWidth
                  sx={btnPrimary}
                  disabled={loading}
                  onClick={submit}
                >
                  {loading ? "변경 중..." : "비밀번호 변경"}
                </Button>
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Box>
  );
}